import {
  CheckSquare,
  Eye,
  MoveRight,
  ArrowLeft,
  GitBranch,
  Sticker,
  GraduationCap,
} from "lucide-react";
import { type TodoItem, TodoTypes, MoveTypes } from "~/api/v2/useMyTodos";
import { formatDate } from "~/helpers/date";
import i18n from "~/i18n";

export const getTodoTypeText = (
  todo: TodoItem,
  currentUserId?: number,
): string => {
  if (todo.todoType === TodoTypes.activeStep) {
    return todo.stepType === "review"
      ? i18n.t(($) => $.dashboard.myTodos.tooltips.reviewStep)
      : i18n.t(($) => $.dashboard.myTodos.tooltips.approveStep);
  }

  if (todo.todoType === TodoTypes.stepToMove) {
    switch (todo.moveType) {
      case MoveTypes.publish:
        return i18n.t(($) => $.dashboard.myTodos.tooltips.publishDocument);
      case MoveTypes.backwards: {
        if (todo.rejector) {
          const fullName =
            `${todo.rejector.firstName} ${todo.rejector.lastName}`.trim();
          if (!fullName) {
            return i18n.t(
              ($) => $.dashboard.myTodos.tooltips.rejectedStepFallback,
            );
          }
          if (currentUserId && todo.rejector.userId === currentUserId) {
            return i18n.t(
              ($) => $.dashboard.myTodos.tooltips.rejectedStepByYou,
            );
          }
          return i18n.t(
            ($) => $.dashboard.myTodos.tooltips.rejectedStepByUser,
            {
              name: fullName,
            },
          );
        }
        return i18n.t(($) => $.dashboard.myTodos.tooltips.rejectedStepFallback);
      }
      case MoveTypes.forwards:
        return i18n.t(($) => $.dashboard.myTodos.tooltips.moveForward);
      default:
        return "";
    }
  }

  if (todo.todoType === TodoTypes.documentToRead) {
    return i18n.t(($) => $.dashboard.myTodos.tooltips.readDocument);
  }

  if (todo.todoType === TodoTypes.invitedToQuiz) {
    return i18n.t(($) => $.dashboard.myTodos.tooltips.takeQuiz);
  }

  return "";
};

export const getTodoIcon = (todo: TodoItem) => {
  if (todo.todoType === TodoTypes.activeStep) {
    return todo.stepType === "review" ? GitBranch : CheckSquare;
  }

  if (todo.todoType === TodoTypes.stepToMove) {
    switch (todo.moveType) {
      case MoveTypes.publish:
        return Sticker;
      case MoveTypes.backwards:
        return ArrowLeft;
      case MoveTypes.forwards:
        return MoveRight;
      default:
        return GitBranch;
    }
  }

  if (todo.todoType === TodoTypes.documentToRead) {
    return Eye;
  }

  if (todo.todoType === TodoTypes.invitedToQuiz) {
    return GraduationCap;
  }
};

const getTodoOrder = (todo: TodoItem): number => {
  switch (todo.todoType) {
    case TodoTypes.stepToMove:
      switch (todo.moveType) {
        case MoveTypes.backwards:
          return 3;
        case MoveTypes.forwards:
          return 4;
        case MoveTypes.publish:
          return 5;
        default:
          return 7;
      }
    case TodoTypes.activeStep:
      switch (todo.stepType) {
        case "approval":
          return 1;
        case "review":
          return 2;
        default:
          return 7;
      }
    case TodoTypes.documentToRead:
      return 6;
    case TodoTypes.invitedToQuiz:
      return 7;
    default:
      return 8;
  }
};

export const getIconColor = (todo: TodoItem): string => {
  if (todo.todoType === TodoTypes.activeStep) {
    return todo.stepType === "review" ? "text-info" : "text-category-approval";
  }

  if (todo.todoType === TodoTypes.stepToMove) {
    switch (todo.moveType) {
      case MoveTypes.publish:
        return "text-success";
      case MoveTypes.backwards:
        return "text-destructive";
      case MoveTypes.forwards:
        return "text-warning";
      default:
        return "text-muted-foreground";
    }
  }

  if (todo.todoType === TodoTypes.documentToRead) {
    return "text-category-read";
  }

  if (todo.todoType === TodoTypes.invitedToQuiz) {
    return "text-info";
  }

  return "text-muted-foreground";
};

export const getIconBackgroundColor = (todo: TodoItem): string => {
  if (todo.todoType === TodoTypes.activeStep) {
    return todo.stepType === "review"
      ? "bg-info/10"
      : "bg-category-approval/10";
  }

  if (todo.todoType === TodoTypes.stepToMove) {
    switch (todo.moveType) {
      case MoveTypes.publish:
        return "bg-success/10";
      case MoveTypes.backwards:
        return "bg-destructive/10";
      case MoveTypes.forwards:
        return "bg-warning/10";
      default:
        return "bg-muted/50";
    }
  }

  if (todo.todoType === TodoTypes.documentToRead) {
    return "bg-category-read/10";
  }

  if (todo.todoType === TodoTypes.invitedToQuiz) {
    return "bg-info/10";
  }

  return "bg-muted/50";
};

// Returns just the date string (for sorting/comparison)
export const getTodoDate = (todo: TodoItem): string | null => {
  switch (todo.todoType) {
    case TodoTypes.documentToRead: {
      return todo.adoptedAt ?? todo.publishedAt ?? null;
    }
    case TodoTypes.invitedToQuiz: {
      return todo.createdAt;
    }
    case TodoTypes.activeStep:
    case TodoTypes.stepToMove: {
      return todo.dueDate ?? todo.documentVersionReviewTo ?? null;
    }
    default:
      return null;
  }
};

const getTodoLabel = (todo: TodoItem): string | null => {
  switch (todo.todoType) {
    case TodoTypes.documentToRead: {
      if (todo.adoptedAt) {
        return i18n.t(($) => $.dashboard.myTodos.dateLabels.adopted);
      } else if (todo.publishedAt) {
        return i18n.t(($) => $.dashboard.myTodos.dateLabels.published);
      }
      break;
    }
    case TodoTypes.invitedToQuiz: {
      return i18n.t(($) => $.dashboard.myTodos.dateLabels.created);
    }
    case TodoTypes.activeStep:
    case TodoTypes.stepToMove: {
      if (todo.dueDate) {
        return i18n.t(($) => $.dashboard.myTodos.dateLabels.stepDueAt);
      } else if (todo.documentVersionReviewTo) {
        return i18n.t(
          ($) => $.dashboard.myTodos.dateLabels.documentVersionReviewTo,
        );
      }
      break;
    }
  }

  return null;
};

export const getTodoDateString = (todo: TodoItem): string | null => {
  const todoLabel = getTodoLabel(todo);
  const todoDate = getTodoDate(todo);

  return todoLabel && todoDate
    ? `${todoLabel}: ${formatDate(todoDate, "date")}`
    : null;
};

export const compareTodoOrder = (a: TodoItem, b: TodoItem): number => {
  return getTodoOrder(a) - getTodoOrder(b);
};

export const getTodoNavigationPath = (todo: TodoItem): string => {
  switch (todo.todoType) {
    case TodoTypes.documentToRead:
      return `/documents/${todo.documentId}?versionId=${todo.documentVersionId}&tab=reading`;
    case TodoTypes.invitedToQuiz:
      return `/anchoring/${todo.quizId}`;
    default:
      return `/documents/${todo.documentId}?tab=workflow`;
  }
};
